import { sprite } from '../kit';
import { C, type Pix } from '../pix';
import { registerIcons } from '../store';
import { gem, R, ramp, type Ramp } from './craft';

/**
 * Hand-made item pictures: pendants (เครื่องห้อย). Every pendant hangs from the same short cord and
 * knot at the top so the slot reads at a glance; the trophy pendants (sig-*, one per monster family)
 * are each that creature's keepsake: a fang, a talisman, a scale.
 */

const bone = ramp('#e8e0c8');
const moonBone = ramp('#c8d8f0');
const whiteJade = ramp('#e8ece0');
const paleJade = ramp('#8ad0a8');
const cord = ramp('#a83a2a');
const indigo = ramp('#2a3a7a');
const stone = ramp('#7a7480');
const talisman = ramp('#e8c84a');
const iceR = ramp('#a8e0f8');
const venom = ramp('#6ad04a');
const glass = ramp('#c8e0e8');
const sand = ramp('#c89a4a');
const spotHide = ramp('#d8a04a');
const blood = ramp('#b02a3a');
const silk = ramp('#e0e4ec');
const chitin = ramp('#6a2a2a');
const olive = ramp('#8a9a3a');
const fox = ramp('#a060d0');
const wood = R.wood;

type Pt = [number, number];

/** The shared hang: a cord from the top edge to a knot, with a bead under it. Returns the knot's y. */
function hang(p: Pix, x: number, y1: number, bead: Ramp = R.gold): number {
  p.line(x - 2, 1, x, y1 - 2, cord.d1);
  p.line(x + 2, 1, x, y1 - 2, cord.b);
  p.rect(x - 1, y1 - 2, 3, 2, cord.b);
  p.px(x + 1, y1 - 2, cord.l1);
  p.px(x, y1, bead.b).px(x + 1, y1, bead.l2);
  return y1;
}

/**
 * A tapering curved spike (fang, claw, tusk, stinger) along a quadratic curve from its root
 * (x0,y0) through (cx,cy) to its point (x1,y1), `w` wide at the root. Shade on the left of the
 * direction of travel, light on the right.
 */
function spike(p: Pix, x0: number, y0: number, cx: number, cy: number, x1: number, y1: number, w: number, m: Ramp): void {
  const n = 24;
  const at = (t: number): Pt => [
    (1 - t) ** 2 * x0 + 2 * (1 - t) * t * cx + t * t * x1,
    (1 - t) ** 2 * y0 + 2 * (1 - t) * t * cy + t * t * y1,
  ];
  const left: Pt[] = [];
  const right: Pt[] = [];
  const lit: Pt[] = [];
  for (let i = 0; i <= n; i++) {
    const t = i / n;
    const [x, y] = at(t);
    const [xa, ya] = at(Math.min(1, t + 0.02));
    const [xb, yb] = at(Math.max(0, t - 0.02));
    const L = Math.hypot(xa - xb, ya - yb) || 1;
    const nx = -(ya - yb) / L;
    const ny = (xa - xb) / L;
    const hw = (w / 2) * (1 - t) ** 0.8;
    left.push([x + nx * hw, y + ny * hw]);
    right.push([x - nx * hw, y - ny * hw]);
    lit.push([x - nx * hw * 0.35, y - ny * hw * 0.35]);
  }
  p.poly([...left, ...[...right].reverse()], m.d1);
  p.poly([...lit, ...[...right].reverse()], m.b);
  p.path(lit.slice(1, n - 3), m.l1);
  p.px(...at(1), m.l2);
}

registerIcons('item', {
  // หยกห้อยเอวสีขาว: a plain white jade plaque, a cloud cut at its top, a red tassel below.
  'plain-pendant': (p) => {
    hang(p, 16, 6);
    p.poly(
      [
        [10, 8],
        [22, 8],
        [23, 10],
        [23, 22],
        [21, 24],
        [11, 24],
        [9, 22],
        [9, 10],
      ],
      whiteJade.d1,
    );
    p.poly(
      [
        [12, 9],
        [22, 9],
        [22, 22],
        [12, 22],
      ],
      whiteJade.b,
    );
    p.line(11, 9, 21, 9, whiteJade.l2);
    p.line(22, 10, 22, 21, whiteJade.l1);
    // A carved inner border.
    p.path(
      [
        [12, 12],
        [12, 20],
        [20, 20],
      ],
      whiteJade.d1,
    );
    p.path(
      [
        [13, 11],
        [20, 11],
        [20, 19],
      ],
      whiteJade.l2,
    );
    p.px(16, 15, whiteJade.d1).px(17, 15, whiteJade.d1).px(16, 16, whiteJade.d2);
    // Tassel.
    p.rect(15, 24, 3, 2, R.gold.b);
    for (let k = -2; k <= 2; k++) p.line(16 + k * 0.5, 26, 16 + k, 30, k < 0 ? cord.d1 : cord.b);
  },
  // หยกห้อยลายเมฆาวน (epic): a jade ruyi-cloud pendant: one deep-carved scroll, its tail sweeping off.
  'cloud-pendant': (p) => {
    hang(p, 14, 5);
    // The scroll head, a smaller curl above right, and the tail.
    p.poly(
      [
        [16, 18],
        [22, 15],
        [28, 18],
        [29, 23],
        [25, 25],
        [20, 23],
      ],
      paleJade.d1,
    );
    p.ell(13, 16, 7.5, 7, paleJade.d1);
    p.ell(22, 11, 4.5, 4, paleJade.d1);
    p.ell(13.5, 15.5, 6.5, 6, paleJade.b);
    p.ell(22.5, 10.5, 3.5, 3, paleJade.b);
    p.poly(
      [
        [17, 18],
        [22, 16],
        [27, 19],
        [28, 22],
        [25, 23],
        [21, 21],
      ],
      paleJade.b,
    );
    p.path(
      [
        [20, 17],
        [24, 18],
        [27, 21],
      ],
      paleJade.l1,
    );
    // Carved spirals: one big, one small.
    p.ring(13, 16, 4, 3.8, paleJade.d2, -0.6, 4.6);
    p.ring(13, 16, 2, 1.8, paleJade.d2, 2.4, 6.6);
    p.px(13, 16, paleJade.l1);
    p.ring(22, 11, 2, 1.8, paleJade.d2, 0.4, 4.8);
    p.ring(13.5, 15.5, 6.5, 6, paleJade.l2, -2.3, -0.8);
    p.ring(22.5, 10.5, 3.5, 3, paleJade.l2, -2.3, -0.5);
    p.rect(12, 7, 5, 2, R.gold.b);
    p.line(12, 7, 16, 7, R.gold.l2);
    // Tassel.
    p.rect(12, 23, 3, 2, R.gold.b);
    p.px(14, 23, R.gold.l2);
    for (let k = -2; k <= 2; k++) p.line(13 + k * 0.5, 25, 13 + k, 30, k < 0 ? cord.d1 : cord.b);
  },
  // เขี้ยวเงาจันทร์: a wolf fang threaded on a cord, a cold moon sheen on it and a crescent behind.
  'sig-wolf': (p) => {
    p.ring(20, 15, 8, 8, ramp('#5a7ab8').b, 1.9, 4.5);
    p.ring(20.6, 15, 7.6, 7.6, ramp('#5a7ab8').d1, 1.9, 4.5);
    hang(p, 13, 7);
    p.rect(11, 8, 5, 3, cord.d1);
    spike(p, 13, 10, 11, 20, 19, 28, 6, moonBone);
    p.px(14, 9, cord.l1);
  },
  // ป้ายหัวโจรเก่า: a battered wooden tag, knife notches across it.
  'sig-bandit': (p) => {
    hang(p, 16, 6);
    p.poly(
      [
        [12, 7],
        [20, 7],
        [24, 11],
        [24, 28],
        [8, 28],
        [8, 11],
      ],
      wood.d1,
    );
    p.poly(
      [
        [13, 8],
        [20, 8],
        [23, 11],
        [23, 27],
        [11, 27],
        [10, 11],
      ],
      wood.b,
    );
    p.line(20, 8, 23, 11, wood.l2);
    p.line(23, 12, 23, 26, wood.l1);
    p.ell(16, 11, 1.4, 1.4, wood.d2);
    // Grain and knife scars.
    p.line(12, 14, 12, 26, wood.d1);
    p.line(18, 15, 19, 25, wood.d1);
    for (const [x, y] of [
      [10, 18],
      [13, 21],
      [16, 16],
    ] as const) {
      p.line(x, y + 3, x + 6, y, wood.d2);
      p.line(x + 1, y + 3, x + 7, y, wood.l1);
    }
    p.rect(10, 24, 4, 2, C.seal);
  },
  // ชายผ้าปิดหน้าลายคราม: a torn strip of indigo face cloth with a pale wave pattern.
  'sig-masked': (p) => {
    hang(p, 9, 6);
    p.poly(
      [
        [6, 7],
        [26, 9],
        [27, 13],
        [25, 16],
        [28, 19],
        [26, 23],
        [22, 22],
        [20, 26],
        [16, 24],
        [12, 27],
        [9, 23],
        [5, 24],
      ],
      indigo.d1,
    );
    p.poly(
      [
        [9, 8],
        [25, 10],
        [26, 13],
        [24, 16],
        [26, 19],
        [22, 20],
        [10, 20],
      ],
      indigo.b,
    );
    p.line(10, 8, 25, 10, indigo.l1);
    // Pattern: pale porcelain-blue zigzag bands and a scatter of dots.
    for (const y of [12, 18])
      for (let x = 8; x < 25; x += 2) p.px(x, y + ((x >> 1) % 2), ramp('#c8d8f0').b);
    for (const [x, y] of [
      [11, 15],
      [16, 15],
      [21, 16],
    ] as const)
      sprite(p, x, y, ['.w.', 'wWw', '.w.'], { w: ramp('#c8d8f0').d1, W: ramp('#c8d8f0').l1 });
    // Frayed threads at the torn end.
    for (const [x, y] of [
      [13, 27],
      [20, 27],
      [26, 24],
    ] as const)
      p.line(x, y, x - 1, y + 2, indigo.l1);
  },
  // เข็มเงียบเสียงลม: a long black needle, a grey silk thread wound round its eye, a hush of wind.
  'sig-assassin': (p) => {
    for (const [x, y, l] of [
      [4, 10, 7],
      [6, 14, 5],
      [20, 25, 6],
    ] as const) {
      p.line(x, y, x + l, y, ramp('#9aa8c8').d1);
      p.px(x + l, y, ramp('#9aa8c8').l1);
    }
    p.thick(8, 27, 25, 6, R.ink.b, 2);
    p.line(9, 27, 26, 6, R.ink.l2);
    p.px(26, 5, C.white).px(25, 6, R.steel.l2);
    // Eye and wound thread.
    p.ring(8, 27, 2, 2, R.ink.d1);
    for (let k = 0; k < 4; k++) p.line(9 + k * 1.2, 23 - k * 1.4, 13 + k * 1.2, 26 - k * 1.4, silk.b);
    p.path(
      [
        [9, 28],
        [6, 30],
        [3, 28],
        [2, 24],
      ],
      silk.d1,
    );
  },
  // ลูกประคำร้าวหนึ่งเม็ด: a loop of prayer beads, the big guru bead cracked through.
  'sig-monk': (p) => {
    const m = ramp('#8a4a2a');
    for (let k = 0; k < 16; k++) {
      const a = -Math.PI / 2 + (k * Math.PI * 2) / 16;
      if (Math.abs(a - Math.PI / 2) < 0.3) continue;
      gem(p, 16 + Math.cos(a) * 10, 14 + Math.sin(a) * 9, 1.8, m);
    }
    gem(p, 16, 24, 4, ramp('#b07a3a'));
    p.path(
      [
        [15, 20],
        [17, 23],
        [15, 25],
        [17, 28],
      ],
      R.ink.b,
    );
    p.px(18, 22, C.white);
    // Little tassel off the guru bead.
    p.rect(15, 28, 3, 1, R.gold.b);
    p.line(15, 29, 14, 31, cord.d1).line(17, 29, 18, 31, cord.b);
  },
  // กรงเล็บหมีหินผา: one great stone-grey bear claw, its root bound in brown fur.
  'sig-bear': (p) => {
    hang(p, 12, 6);
    spike(p, 12, 10, 22, 14, 20, 29, 9, stone);
    sprite(
      p,
      7,
      7,
      ['..fff.f..', '.fFfffff.', 'fffFfFfff', '.ffdfffd.', '..d..d...'],
      { f: ramp('#6a4a32').b, F: ramp('#6a4a32').l1, d: ramp('#6a4a32').d1 },
    );
    p.line(18, 21, 20, 26, stone.d2);
  },
  // ด้ามมีดพันผ้าแดง: a knife's grip, its blade snapped off, wrapped in faded red cloth.
  'sig-knifer': (p) => {
    // Snapped blade stub.
    p.poly(
      [
        [20, 6],
        [26, 2],
        [27, 5],
        [23, 9],
      ],
      R.steel.d1,
    );
    p.line(21, 6, 26, 3, R.steel.l2);
    p.path(
      [
        [26, 2],
        [25, 4],
        [27, 5],
      ],
      R.steel.d2,
    );
    // Guard.
    p.thick(17, 6, 23, 12, R.iron.d1, 3);
    p.line(18, 6, 24, 12, R.iron.l1);
    // Wrapped grip: diagonal turns of faded red.
    const fade = ramp('#c8584a');
    p.thick(8, 22, 19, 11, fade.d1, 5);
    for (let k = 0; k < 6; k++) {
      const x = 9 + k * 1.9;
      const y = 21 - k * 1.9;
      p.line(x - 1, y - 1, x + 2, y + 2, fade.b);
      p.px(x + 2, y, fade.l1);
    }
    // Pommel ring and a loose end of cloth.
    p.ring(6, 24, 2.5, 2.5, R.iron.b);
    p.px(7, 22, R.iron.l2);
    p.path(
      [
        [11, 20],
        [12, 25],
        [10, 29],
      ],
      fade.d1,
      2,
    );
  },
  // ยันต์เหลืองหน้าผาก: a yellow paper talisman, red brush script down it, its top torn.
  'sig-jiangshi': (p) => {
    p.poly(
      [
        [10, 4],
        [13, 3],
        [16, 5],
        [19, 3],
        [22, 4],
        [22, 29],
        [10, 29],
      ],
      talisman.d1,
    );
    p.rect(11, 5, 10, 23, talisman.b);
    p.line(21, 5, 21, 28, talisman.l1);
    p.line(11, 5, 11, 28, talisman.d1);
    // Red script: a top seal mark, then strokes.
    sprite(
      p,
      12,
      7,
      [
        '.rrrrrr.',
        '.r....r.',
        '.rrrrrr.',
        '...r....',
        '.rrrrrr.',
        '...r.r..',
        '..r..r..',
        '.rr.rrr.',
        '....r...',
        '.rrrrrr.',
        '...r....',
        '..rr.r..',
        '.r...rr.',
        '....r...',
        '..rrr...',
        '....r...',
        '...rr...',
      ],
      { r: C.seal },
    );
    // A crease and a stain.
    p.line(11, 17, 20, 18, talisman.d1);
    p.px(19, 25, talisman.d2).px(18, 26, talisman.d1);
  },
  // ขนขาวต้านลมหนาว: a shaggy lock of white snow-wolf fur, bound at the top, locks hanging uneven.
  'sig-snowwolf': (p) => {
    const frost = ramp('#a8b8e0');
    hang(p, 16, 5);
    sprite(
      p,
      7,
      6,
      [
        '.......rrrr.......',
        '......rRrrr.......',
        '.....wwwwwWw......',
        '....wwwwwWWWw.....',
        '...dwwwwwwWWww....',
        '..ddwwwwwwwWWww...',
        '..dwwwwwwwwwWWw...',
        '.ddwwwwwwwwwwWww..',
        '.dwwwdwwwwwwwwWw..',
        '.dwwdwwwwdwwwwWWw.',
        'ddwwdwwwdwwwwwwWw.',
        'dwwdwwwwdwwwdwwWWw',
        'dwdwwdwwdwwwdwwwWw',
        'dwdwdwwdwwwwdwwwWw',
        '.ddwdwwdwwwdwwwdWw',
        '.d.wd.wdw.wdww.dw.',
        '.d..d.wd..wd.w..w.',
        '....d..d..w...w.w.',
        '.......d..w.......',
        '..........w.......',
      ],
      { w: silk.b, W: C.white, d: frost.b, r: cord.b, R: cord.l1 },
    );
    // Cold wind past it and a snow fleck.
    p.line(2, 14, 6, 14, frost.d1);
    p.line(3, 18, 5, 18, frost.d1);
    p.px(27, 10, iceR.l1).px(28, 22, iceR.l1);
  },
  // ผลึกใจน้ำแข็ง: a cluster of ice crystals held in a cord cage.
  'sig-frostman': (p) => {
    hang(p, 16, 5);
    const prism = (cx: number, top: number, bot: number, hw: number) => {
      p.poly(
        [
          [cx, top],
          [cx + hw, top + hw],
          [cx + hw, bot - hw],
          [cx, bot],
          [cx - hw, bot - hw],
          [cx - hw, top + hw],
        ],
        iceR.d1,
      );
      p.poly(
        [
          [cx, top],
          [cx + hw, top + hw],
          [cx + hw, bot - hw],
          [cx, bot],
        ],
        iceR.b,
      );
      p.line(cx, top + 1, cx, bot - 1, iceR.l2);
      p.line(cx + hw - 1, top + hw, cx + hw - 1, bot - hw, iceR.l1);
    };
    prism(11, 12, 26, 3);
    prism(21, 11, 25, 3);
    prism(16, 6, 29, 4);
    // Cord cage bands.
    p.line(10, 15, 22, 13, cord.b);
    p.line(10, 22, 22, 21, cord.d1);
    p.px(17, 9, C.white).px(22, 14, C.white);
  },
  // งาหมูป่าดื้อรั้น: a short thick boar tusk, its root dark, a band of hide cord round it.
  'sig-boar': (p) => {
    hang(p, 9, 6);
    spike(p, 9, 10, 10, 26, 26, 20, 9, bone);
    p.thick(7, 11, 13, 12, ramp('#5a3a2a').b, 3);
    p.line(7, 11, 13, 11, ramp('#5a3a2a').l1);
    p.px(15, 20, bone.d2).px(16, 22, bone.d1);
  },
  // ต่อมพิษงูเขียว: a stoppered glass vial, a green venom gland floating inside.
  'sig-viper': (p) => {
    hang(p, 16, 5);
    // Cork and neck.
    p.rect(14, 6, 5, 3, R.wood.b);
    p.line(14, 6, 18, 6, R.wood.l1);
    p.rect(14, 9, 5, 3, glass.d1);
    // Body: a round flask.
    p.ell(16, 20, 8, 8, glass.d1);
    p.ell(16, 21, 7, 6.5, venom.d2);
    p.ell(16, 22, 6, 5, venom.d1);
    // The gland: a lit green lobe.
    p.ell(15, 21, 4, 3, venom.b);
    p.ell(16, 20, 2, 1.5, venom.l1);
    p.px(16, 19, venom.l2);
    // Glass glints.
    p.ring(16, 20, 7, 7, glass.l2, -1.5, -0.6);
    p.px(20, 15, C.white);
    // A tiny snake mark on the cork.
    p.px(16, 7, venom.b).px(17, 8, venom.b);
  },
  // หางแมงป่องทราย: a curled scorpion tail, segment by segment, the sting raised.
  'sig-scorpion': (p) => {
    hang(p, 9, 5);
    const segs: [number, number, number][] = [
      [9, 9, 3.2],
      [10, 14, 3.4],
      [12, 19, 3.6],
      [16, 23, 3.8],
      [21, 23, 3.6],
      [24, 19, 3.4],
    ];
    for (const [x, y, r] of segs) {
      p.ell(x, y, r, r, sand.d1);
      p.ell(x + 0.4, y - 0.4, r - 0.9, r - 0.9, sand.b);
      p.px(x + 1, y - 2, sand.l2);
    }
    // The bulb and its hooked sting.
    p.ell(25, 13, 3.2, 3.6, sand.d1);
    p.ell(25.5, 12.5, 2.2, 2.6, sand.l1);
    spike(p, 24, 10, 22, 5, 18, 7, 3, ramp('#5a2a1a'));
    p.px(18, 7, R.red.l1);
  },
  // หนังลายจุดไร้เสียง: a soft patch of leopard hide, rosettes in rows, its edge stitched.
  'sig-leopard': (p) => {
    hang(p, 16, 5);
    p.poly(
      [
        [8, 7],
        [24, 7],
        [27, 12],
        [25, 18],
        [27, 25],
        [21, 29],
        [11, 29],
        [5, 25],
        [7, 18],
        [5, 12],
      ],
      spotHide.d1,
    );
    p.poly(
      [
        [16, 8],
        [24, 8],
        [26, 12],
        [24, 18],
        [26, 24],
        [21, 28],
        [16, 28],
      ],
      spotHide.b,
    );
    p.line(10, 8, 23, 8, spotHide.l1);
    for (const [x, y] of [
      [10, 11],
      [17, 11],
      [22, 15],
      [9, 18],
      [15, 17],
      [20, 22],
      [12, 24],
      [24, 9],
    ] as const)
      sprite(p, x, y, ['.dd.', 'd..d', '.dd.'], { d: ramp('#3a2a20').b });
    for (let x = 9; x < 24; x += 3) p.px(x, 7, R.wood.d2);
  },
  // ปีกค้างคาวสีเลือด: a blood-red bat wing spread open, its finger bones dark.
  'sig-bloodbat': (p) => {
    // Membrane, scalloped along the bottom.
    p.poly(
      [
        [6, 8],
        [28, 5],
        [29, 11],
        [26, 17],
        [22, 15],
        [19, 22],
        [15, 18],
        [11, 25],
        [8, 19],
        [5, 21],
      ],
      blood.d1,
    );
    p.poly(
      [
        [9, 9],
        [27, 6],
        [27, 11],
        [25, 15],
        [22, 13],
        [18, 18],
        [15, 15],
      ],
      blood.b,
    );
    p.line(10, 9, 27, 6, blood.l1);
    // Arm and finger bones from the wrist.
    const wx = 9;
    const wy = 9;
    p.thick(4, 12, wx, wy, R.ink.b, 2);
    for (const [x, y] of [
      [28, 5],
      [26, 16],
      [19, 21],
      [11, 24],
    ] as const)
      p.line(wx, wy, x, y, R.ink.d1);
    p.px(wx, wy - 1, bone.b);
    p.px(5, 6, bone.l1).px(6, 7, bone.b);
  },
  // ใยแมงมุมเหนียวแน่น: a disc of web spun across a little bamboo hoop, a dew bead on a strand.
  'sig-spider': (p) => {
    hang(p, 16, 5);
    p.ell(16, 18, 11, 11, R.wood.d1);
    p.ell(16, 18, 10, 10, ramp('#2a2440').b);
    p.ring(16, 18, 10.5, 10.5, R.wood.l1, -2, -0.2);
    for (let k = 0; k < 8; k++) {
      const a = (k * Math.PI) / 4 + 0.2;
      p.line(16, 18, 16 + Math.cos(a) * 9.5, 18 + Math.sin(a) * 9.5, silk.d1);
    }
    for (const r of [3, 5.5, 8])
      for (let k = 0; k < 8; k++) {
        const a0 = (k * Math.PI) / 4 + 0.2;
        const a1 = a0 + Math.PI / 4;
        p.line(16 + Math.cos(a0) * r, 18 + Math.sin(a0) * r, 16 + Math.cos(a1) * r, 18 + Math.sin(a1) * r, silk.b);
      }
    p.px(16, 18, silk.l2);
    p.px(21, 13, C.white).px(20, 14, iceR.l1);
  },
  // ข้อตะขาบร้อยขา: four armoured centipede segments and the head, yellow legs splayed.
  'sig-centipede': (p) => {
    hang(p, 7, 5);
    const segs: [number, number][] = [
      [9, 10],
      [13, 15],
      [17, 19],
      [21, 23],
      [25, 27],
    ];
    const leg = ramp('#e8b43a');
    for (const [x, y] of segs) {
      p.line(x - 6, y + 3, x - 1, y, leg.d1);
      p.line(x + 6, y - 3, x + 1, y, leg.b);
      p.px(x - 6, y + 3, leg.l1).px(x + 6, y - 3, leg.l2);
    }
    for (const [x, y] of segs) {
      p.ell(x, y, 4, 3.4, chitin.d1);
      p.ell(x + 0.6, y - 0.6, 3, 2.4, chitin.b);
      p.line(x - 1, y - 2, x + 2, y - 2, chitin.l1);
      p.px(x + 2, y - 2, chitin.l2);
    }
    // Fangs on the first plate.
    p.px(5, 12, leg.l1).px(6, 13, leg.b);
    p.px(11, 7, leg.l1);
  },
  // เกล็ดงูรัดแน่น: one great python scale over a fan of smaller ones, olive and gold.
  'sig-python': (p) => {
    hang(p, 16, 5);
    const scale = (cx: number, cy: number, hw: number, h: number, m: Ramp) => {
      p.poly(
        [
          [cx, cy - h],
          [cx + hw, cy - h * 0.2],
          [cx + hw * 0.6, cy + h * 0.6],
          [cx, cy + h],
          [cx - hw * 0.6, cy + h * 0.6],
          [cx - hw, cy - h * 0.2],
        ],
        m.d1,
      );
      p.poly(
        [
          [cx, cy - h + 1],
          [cx + hw - 1, cy - h * 0.2],
          [cx + hw * 0.6 - 1, cy + h * 0.6 - 1],
          [cx, cy + h - 2],
        ],
        m.b,
      );
      p.line(cx, cy - h + 2, cx, cy + h - 3, m.l1);
    };
    scale(9, 22, 5, 6, olive);
    scale(23, 22, 5, 6, olive);
    scale(16, 25, 5, 5, olive);
    scale(16, 16, 8, 10, ramp('#b8a04a'));
    p.path(
      [
        [12, 14],
        [16, 11],
        [20, 14],
      ],
      ramp('#b8a04a').l2,
    );
  },
  // หางจิ้งจอกเก้าสี: a bushy fox-spirit tail curling round, its fur shading through colour bands.
  'sig-foxspirit': (p) => {
    const bands: Ramp[] = [fox, ramp('#d060a8'), ramp('#f08a4a'), ramp('#f0c860'), ramp('#f4f0f0')];
    const band = (t: number) => bands[Math.min(4, Math.floor(t * 5))]!;
    const at = (t: number): Pt => {
      const a = Math.PI * 0.8 - t * Math.PI * 1.25;
      return [16 + Math.cos(a) * 9, 16 + Math.sin(a) * 10];
    };
    const rad = (t: number) => 1.6 + 5.4 * Math.sin(Math.min(1, t * 0.95 + 0.1) * Math.PI) ** 0.7;
    const N = 48;
    for (let i = 0; i <= N; i++) {
      const t = i / N;
      const [x, y] = at(t);
      const r = rad(t);
      p.ell(x, y, r, r, band(t).d1);
      // Fur tufts flicking outward.
      if (i % 4 === 2) {
        const [cx, cy] = [16, 16];
        const L = Math.hypot(x - cx, y - cy) || 1;
        p.line(x, y, x + ((x - cx) / L) * (r + 2), y + ((y - cy) / L) * (r + 2), band(t).d1);
      }
    }
    for (let i = 0; i <= N; i++) {
      const t = i / N;
      const [x, y] = at(t);
      const r = rad(t);
      p.ell(x + r * 0.25, y - r * 0.25, r * 0.62, r * 0.62, band(t).b);
    }
    for (let i = 4; i < N - 2; i += 4) {
      const t = i / N;
      const [x, y] = at(t);
      const r = rad(t);
      p.line(x + r * 0.2, y - r * 0.5, x + r * 0.6, y - r * 0.1, band(t).l1);
    }
    p.px(...at(1), C.white);
    // Bound root with a cord.
    const [rx, ry] = at(0);
    p.rect(rx - 2, ry - 1, 4, 3, cord.b);
    p.px(rx + 1, ry - 1, cord.l1);
  },
  // เขี้ยวพยัคฆ์ขาว: a great white tiger fang, a gold cap at its root, black stripes engraved.
  'sig-whitetiger': (p) => {
    hang(p, 16, 5);
    spike(p, 16, 9, 22, 18, 14, 30, 10, ramp('#f0f0f4'));
    p.ell(16, 9, 5.5, 2.5, R.gold.d1);
    p.ell(16.5, 8.5, 4.5, 1.8, R.gold.b);
    p.line(13, 8, 19, 8, R.gold.l2);
    for (const [x0, y0, x1, y1] of [
      [13, 13, 17, 15],
      [14, 18, 19, 19],
      [15, 23, 18, 23],
    ] as const)
      p.line(x0, y0, x1, y1, R.ink.b);
  },
});
